import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { Router } from '@angular/router';
import { ConsumerService } from '../../common/services/data-model/app/consumer/consumer.service';
import { MembershipName } from '../../models/membership';
import {
  DateUtility,
  getFormattedFullName,
  isNullOrUndefined,
  isStringNullUndefinedOrEmpty,
  openDialog
} from '../../common/utility';
import { EligibilityService } from '../../common/services/data-model/app/eligibility/eligibility.service';
import {
  Eligibility,
  EligibilityCoverage,
  EligibilityMessage,
  EligibilityProductPackage,
  EligibleItem,
  EligibleItemVsrDto
} from '../../models/eligibility';
import { DatePickerConfiguration } from '../../common/components/date-picker/date-picker.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import {
  ApplicationConstants,
  AuthorizedItemStatus,
  ErrorTypes,
  SessionStorageKeys
} from '../../common/constants/application.constants';
import domainSwap from '../../common/utility/domain-swap/domain-swap';
import { CustomValidatorsService } from '../../common/services/support/custom-validators/custom-validators.service';
import { AuthorizationsService } from '../../common/services/data-model/app/authorizations/authorizations.service';
import { CreateVisionServiceRequest } from '../../models/VisionServiceRequestResults';
import { MatDialog } from '@angular/material/dialog';
import { BusinessMemberPoliciesResults, ConsumerName } from '../../models/consumer';
import { Link } from '../../models/link';
import { DrReportsNavigationService } from '../../common/services/support/dr-reports-navigation/dr-reports-navigation.service';

export enum EligibilityStatusDescription {
  YES = 'Yes',
  AUTHORIZED = 'Authorized',
}

@Component({
  selector: 'app-coverage-summary',
  templateUrl: './coverage-summary.component.html',
  styleUrls: ['./coverage-summary.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CoverageSummaryComponent implements OnInit, OnDestroy {

  constructor(
    private router: Router,
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private eligibilityService: EligibilityService,
    private consumerService: ConsumerService,
    private authorizationService: AuthorizationsService,
    private customValidatorsService: CustomValidatorsService,
    private drReportsNavigationService: DrReportsNavigationService
  ) {
  }

  /***** START - PRIVATE MEMBERS *****/
  private _memberName: MembershipName = {firstName: '', lastName: ''};
  private _consumerName: ConsumerName = {firstName: '', lastName: '', firstNameSrc: '', lastNameSrc: ''};
  private _memberEligibility: Eligibility;
  private _loadingEligibility: boolean;
  private _lastValidDateOfServiceWherePatientIsEligibileForServices: string;

  /***** START - PUBLIC MEMBERS *****/
  today = new Date();
  coverageSummaryFormGroup: FormGroup;
  dateOfService = new Date();
  datePickerConfiguration: DatePickerConfiguration;

  // Medicaid Repair and Replace
  MRRDropdownItems: any[] = [
    {value: 'Lost, stolen, or broken'},
    {value: 'Prescription Change'},
    {value: 'Visual necessity'},
  ];
  allMrrEligibleItems: EligibleItem[] = [
    {description: 'Exam', productCatalogKey: 'GenExam', authorizable: true, statusDescription: EligibilityStatusDescription.YES, isSelected: false, inLieuOfIsSelected: false},
    {description: 'Lens', productCatalogKey: 'Lens', authorizable: true, statusDescription: EligibilityStatusDescription.YES, isSelected: false, inLieuOfIsSelected: false},
    {description: 'Frame', productCatalogKey: 'Frame', authorizable: true, statusDescription: EligibilityStatusDescription.YES, isSelected: false, inLieuOfIsSelected: false},
    {description: 'Contact Lens', productCatalogKey: 'ContactLens', authorizable: true, statusDescription: EligibilityStatusDescription.YES, isSelected: false, inLieuOfIsSelected: false},
    {description: 'Contact Lens Service', productCatalogKey: 'ContactLensService', authorizable: true, statusDescription: EligibilityStatusDescription.YES, isSelected: false, inLieuOfIsSelected: false}
  ];

  // Contact Lens Remaining Allowance
  clraEligibleItem: EligibleItem = {
    description: 'Contact Lens',
    statusDescription: EligibilityStatusDescription.YES,
    authorizable: true,
    productCatalogKey: 'ContactLens',
    isSelected: false
  };

  public readonly ApplicationConstants = ApplicationConstants;

  /***** END - PUBLIC MEMBERS *****/


  /***** START - PROPERTY ACCESSORS *****/
  get memberEligibility(): Eligibility {
    return this._memberEligibility;
  }

  set memberEligibility(eligibility: Eligibility) {
    this._memberEligibility = eligibility;
  }

  get loadingEligibility(): boolean {
    return this._loadingEligibility;
  }

  set loadingEligibility(loadingEligibility: boolean) {
    this._loadingEligibility = loadingEligibility;
  }

  get lastValidDateOfServiceWherePatientIsEligibileForServices(): string {
    return this._lastValidDateOfServiceWherePatientIsEligibileForServices;
  }

  set lastValidDateOfServiceWherePatientIsEligibileForServices(lastValidDateOfServiceWherePatientIsEligibileForServices: string) {
    this._lastValidDateOfServiceWherePatientIsEligibileForServices = lastValidDateOfServiceWherePatientIsEligibileForServices;
  }

  get eligibilityProductPackages(): EligibilityProductPackage[] {
    if (this.memberEligibility.productPackageViewModelList) {
      return this.memberEligibility.productPackageViewModelList;
    }
  }

  get clientMessages(): string[] {
    if (this.memberEligibility.clientMessages) {
      return this.memberEligibility.clientMessages;
    }
  }

  get memberName(): MembershipName {
    return this._memberName;
  }

  set memberName(name: MembershipName) {
    this._memberName = name;
  }

  get consumerName(): ConsumerName {
    return this._consumerName;
  }

  set consumerName(name: ConsumerName) {
    this._consumerName = name;
  }

  /***** END - PROPERTY ACCESSORS *****/

  /***** START - PRIVATE METHODS *****/
  private isEligibleItemAuthorizableAndNotSelected = (eligibleItem: EligibleItem): boolean =>
    eligibleItem && eligibleItem.authorizable && !eligibleItem.isSelected

  private closeAllPendingDialogs = () => {
    if (this.dialog.openDialogs && this.dialog.openDialogs.length > 0) {
      this.dialog.closeAll();
    }
  }

  private continueIssueAuthorizationNavigation(): void {
    this.closeAllPendingDialogs();

    if (!isNullOrUndefined(this.consumerService.businessMemberRetrieveResults)
      && !isNullOrUndefined(this.consumerService.businessMemberRetrieveResults.fehb)
      && this.consumerService.businessMemberRetrieveResults.fehb.fehbEnrollInd
      && !isNullOrUndefined(this.consumerService.businessMemberRetrieveResults.fehb.fehbPlanLink)) {
      this.consumerService.retrieveClientWebFEHBPlan(this.consumerService.businessMemberRetrieveResults.fehb.fehbPlanLink.href).subscribe((federalEnrolleeHealthBenefitPlan) => {
        if (federalEnrolleeHealthBenefitPlan && federalEnrolleeHealthBenefitPlan.planName) {
          this.router.navigate([ApplicationConstants.routing.secure.cobCoveragePageUrl]);
        } else {
          this.router.navigate([[ApplicationConstants.routing.secure.baseRoute, ApplicationConstants.routing.secure.authorizationConfirmation].join('/')]);
        }
      });
    } else {
      this.router.navigate([[ApplicationConstants.routing.secure.baseRoute, ApplicationConstants.routing.secure.authorizationConfirmation].join('/')]);
    }
  }

  /**
   * Prototypes the eligible items on the response from eligibility-web with a view model property for checkboxes.
   *
   * @see CoverageSummaryComponent#toggleAuthorizeBenefitCheckboxForAllProductPackageEligibleItems
   * @see CoverageSummaryComponent#toggleAuthorizedBenefitCheckboxForSingleEligibleItem
   * @see CoverageSummaryComponent#toggleAuthorizeBenefitCheckboxForAllProductPackageEligibleItemsMrr
   * @see CoverageSummaryComponent#toggleAuthorizedBenefitCheckboxForSingleEligibleItemMrr
   */
  private setDefaultAuthorizedBenefitsCheckboxForEachProductPackageEligibleItem(): void {

    // retrieves list of all CLRA-benefit-available productPackage names and corresponding $ amounts from eligibility response.
    const clraPackageNameAndAmount: Map<string, string> = this.getClraPackagesNameAndAmount(this.memberEligibility.messages);

    this.eligibilityProductPackages.forEach((productPackage: EligibilityProductPackage) => {
      productPackage.eligibleItemInLieuOfs = new Map<string, string[]>();
      // Check if we have coverages returned for the product package
      if (productPackage.coverages && productPackage.coverages.length > 0) {
        // Iterate through the coverages on the product package to check if there are any in lieu of relation to the eligible item that has just been updated
        productPackage.coverages.forEach((coverageItem: EligibilityCoverage) => {
          // If the coverage has a productCatalogKey and an inLieuOf with product catalog key(s) then we can set the inLieuOf relation in the eligibleItemInLieuOfs map
          if (coverageItem && coverageItem.productCatalogKeys && coverageItem.productCatalogKeys.length > 0 &&
            coverageItem.inLieuOf && coverageItem.inLieuOf.productCatalogKeys && coverageItem.inLieuOf.productCatalogKeys.length > 0) {
            productPackage.eligibleItemInLieuOfs.set(coverageItem.productCatalogKeys[0], coverageItem.inLieuOf.productCatalogKeys);
          }
        });
      }

      // CLRA Logic
      if (isNullOrUndefined(clraPackageNameAndAmount)) {
        productPackage.isClraFlow = false;
        productPackage.clraAmount = null;
        productPackage.clraItem = null;
      } else {
        productPackage.isClraFlow = clraPackageNameAndAmount.has(productPackage.name);
        if (productPackage.isClraFlow) {
          productPackage.clraItem = this.clraEligibleItem;
          productPackage.clraAmount = clraPackageNameAndAmount.get(productPackage.name);
        }
      }

      // MRR logic
      productPackage.isMrrFlow = this.hasEligibilityMessages() ? this.checkIfMrrFlow(productPackage) : false;
      // MRR - create MRR EligibleItems from items listed in message and initialize.
      if (productPackage.isMrrFlow) {
        productPackage.allAvailableMrrServices = false;
        productPackage.mrrDropdownSelection = '';
        // get the MRR benefits
        const mrrBenefitsAvailable: string[] = this.getMRREligibleItems(productPackage);
        // creates a corresponding EligibleItem object for each MRR item and attaches it to productPackage.mrrEligibleItem
        productPackage.mrrEligibleItems = [];
        if (mrrBenefitsAvailable && mrrBenefitsAvailable.length > 0) {
          mrrBenefitsAvailable.forEach(mrrBenefit => {
            productPackage.mrrEligibleItems.push(this.allMrrEligibleItems.find(item => item.productCatalogKey === mrrBenefit));
          });
        }

        productPackage.mrrEligibleItems.forEach((eligibleItem: EligibleItem) => {
          eligibleItem.isSelected = false;
          if (isNullOrUndefined(productPackage.hasAnyAvailableMrrServices)) {
            productPackage.hasAnyAvailableMrrServices = productPackage.mrrEligibleItems.length > 0 ? true : undefined;
          }
        });
      }

      // Initialize all available services checkbox
      productPackage.allAvailableServices = false;
      if (productPackage.eligibleItems && productPackage.eligibleItems.length > 0) {
        productPackage.eligibleItems.forEach((eligibleItem: EligibleItem) => {
          // Initialize each individual service checkbox
          eligibleItem.isSelected = false;

          // Initialize a view property to check if a product package has any available services
          if (isNullOrUndefined(productPackage.hasAnyAvailableServices)) {
            productPackage.hasAnyAvailableServices = this.isAvailableToAuthorize(eligibleItem) ? true : undefined;
          }
        });
      }
    });
  }

  // checks if PBELIG0012 is present in eligibility response.
  private checkIfMrrFlow(productPackage: EligibilityProductPackage): boolean {
    return productPackage.name === ApplicationConstants.medicaidRepairAndReplace.medicaid &&
      this.memberEligibility.messages.some(message => message.code === ApplicationConstants.medicaidRepairAndReplace.PBELIG0012);
  }

  // checks in the PBELIG0012 message for which items are available for MRR
  private getMRREligibleItems(productPackage: EligibilityProductPackage): string[] {
    const PBELIG0012_message: string = this.memberEligibility.messages.filter(message => message.code === ApplicationConstants.medicaidRepairAndReplace.PBELIG0012)[0].description;
    // PBELIG0012_message similar to: Medicaid Repair/Replace is available for Lens and ContactLens. Determine if patient meets criteria.
    const cleanedString = PBELIG0012_message.replace(/[.,:]/g, '');
    const messageWords: string[] = cleanedString.split(/\s+/);
    return this.parsedMrrBenefits(ApplicationConstants.medicaidRepairAndReplace.mrrProductCatalogKeys, messageWords);
  }

  // this method checks parses the MRR benefits available from the PBELIG0012_message.
  private parsedMrrBenefits(x: string[], y: string[]): string[] {
    const parsedMrrBenefits: string[] = [];

    for (let i = 0; i < x.length; i++) {
      for (let j = 0; j < y.length; j++) {
        if (x[i] === y[j]) {
          parsedMrrBenefits.push(x[i]);
          break;
        }
      }
    }
    return parsedMrrBenefits;
  }

  // Checks the Eligibility response and returns all the CLRA packages and amounts, otherwise null.
  getClraPackagesNameAndAmount(messages: EligibilityMessage[]): Map<string, string> {
    if (isNullOrUndefined(messages) || messages.length < 1) {
      return null;
    }

    const CLRA_messages_descriptions: string[] = messages.filter(message => message.code === ApplicationConstants.contactLensRemainingAllowance.PBELIG0011).map(msg => msg.description);

    if (isNullOrUndefined(CLRA_messages_descriptions) || CLRA_messages_descriptions.length < 1) {
      return null;
    }

    // Regular Expressions to parse the exact PackageName and $ Amount from the messages.
    // Sample Description: The patient has used their SignatureChoice contact lens benefit. There is remaining allowance of $20.00 for ContactLens.
    const packageNamesRegex = /\b(Signature|SignatureChoice|Advantage|EnhancedAdvantage|VSPEssentialsPlan|AdditionalPair)\b/;
    const dollarAmountRegex = /\$\d+(?:,\d{3})*(?:\.\d{2})?/;

    const clraPackageAndAmount = new Map<string, string>();

    CLRA_messages_descriptions.forEach(description => {
      // checks each message description and read the string to match productPackageName in the message.
      const packageName = description.match(packageNamesRegex);
      const amount = description.match(dollarAmountRegex);

      if (!isNullOrUndefined(packageName) && !isNullOrUndefined(amount)) {
        clraPackageAndAmount.set(packageName[0], amount[0]);
      }
    });

    return clraPackageAndAmount.size > 0 ? clraPackageAndAmount : null;
  }

  private buildDatePickerConfiguration(): void {
    this.datePickerConfiguration = {
      control: this.coverageSummaryFormGroup.controls.dateOfService,
      controlName: 'dateOfService',
      errorWrapperId: {
        defaultValidations: 'coverage-summary-date-of-service-date-error',
        minDate: 'coverage-summary-date-of-service-min-date-error'
      },
      attributes: {
        id: 'date-of-service'
      },
      customErrorMessages: [
        {
          validatorType: ErrorTypes.Required,
          errorMessage: 'Please enter the date of service'
        },
        {
          validatorType: ErrorTypes.InvalidDateFormat,
          errorMessage: 'Date of Service must be in MM/DD/YYYY format'
        }
      ]
    };
  }

  private buildForm(): void {
    this.coverageSummaryFormGroup = this.formBuilder.group({
      dateOfService: [DateUtility.buildFriendlyDateFromJsDate(sessionStorage.getItem(SessionStorageKeys.MemberDateOfService)),
        [
          Validators.required,
          this.customValidatorsService.dateFormatAndValidity,
          this.customValidatorsService.MinDate(ApplicationConstants.minDate)
        ]]
    });
  }

  private updateVSRLinkReturnedFromEligibilityWithDateOfService(vsrLink: string): string {
    // Instantiate the link as a url
    const vsrLinkAsUrl: URL = new URL(vsrLink);

    // If the date of service is valid, update the link with it
    if (!isStringNullUndefinedOrEmpty(this.lastValidDateOfServiceWherePatientIsEligibileForServices) && this.coverageSummaryFormGroup.valid) {
      vsrLinkAsUrl.searchParams.set('effectiveDate', DateUtility.buildYyyyMmDdDateFromDate(this.lastValidDateOfServiceWherePatientIsEligibileForServices));
      return vsrLinkAsUrl.toString();
    }

    // Default to returning the link passed from eligibility if date of service is not available
    return vsrLink;
  }

  /***** END - PRIVATE FUNCTIONS *****/


  /***** START - PUBLIC FUNCTIONS *****/
  // checks if there are any client messages to show
  hasClientMessages(): boolean {
    return !isNullOrUndefined(this.memberEligibility) &&
      !isNullOrUndefined(this.memberEligibility.clientMessages) &&
      this.memberEligibility.clientMessages.length > 0;
  }

  // checks if Eligibility response contains any PBELIG00XX messages
  hasEligibilityMessages(): boolean {
    return !isNullOrUndefined(this.memberEligibility) &&
      !isNullOrUndefined(this.memberEligibility.messages) &&
      this.memberEligibility.messages.length > 0;
  }

  // build the full name of the patient/member
  getFullName(): string {
    const middleInitial = this.memberName.middleInitial ? this.memberName.middleInitial : null;
    return getFormattedFullName(this.memberName.firstName, this.memberName.lastName, middleInitial);
  }

  eligibilityProductPackagesOnlyAuthorizedItems(businessName: string): any {
    const packageResult = this.eligibilityProductPackages.find((pkg: EligibilityProductPackage) => {
      return pkg.businessName === businessName;
    });

    return packageResult.eligibleItems.filter(eligibleItem => eligibleItem.authorizable);
  }

  private loadEligibility(): void {
    this.loadingEligibility = true;
    // Retrieve eligibility stored in service
    this.memberEligibility = this.eligibilityService.memberEligibility;

    if (!this.memberEligibility) {
      // if there is no eligibility stored in the service, then check session storage
      const sessionStoredEligibilityRetrieveLink = this.eligibilityService.getEligibilityRetrieveLinkFromSessionStorage();
      // if we have elgibility link in session storage, then recall the eligibility API to get data.
      if (sessionStoredEligibilityRetrieveLink && sessionStoredEligibilityRetrieveLink.href) {
        // Calling LoadingModalComponent for Spinner (Preload Icon)
        openDialog('Loading Coverage...', this.dialog);
        this.eligibilityService.retrieveMemberEligibilityFromConsumerLink(sessionStoredEligibilityRetrieveLink.href).subscribe((memberEligibility: Eligibility) => {
          if (memberEligibility) {
            this.lastValidDateOfServiceWherePatientIsEligibileForServices = this.coverageSummaryFormGroup.controls.dateOfService.value;
            this.memberEligibility = memberEligibility;
            if (this.eligibilityProductPackagesOnlyAuthorizedItems) {
              this.setDefaultAuthorizedBenefitsCheckboxForEachProductPackageEligibleItem();
            }
          }
        }, () => {
          // on observer error
        }, () => {
          // Close the dialog when the observer completes
          this.dialog.closeAll();
          this.loadingEligibility = false;
        });
      } else {
        this.router.navigate([ApplicationConstants.routing.secure.memberSearchPageUrl]);
      }
    } else {
      if (this.eligibilityProductPackagesOnlyAuthorizedItems) {
        this.setDefaultAuthorizedBenefitsCheckboxForEachProductPackageEligibleItem();
      }
      this.loadingEligibility = false;
    }
  }

  private reloadEligibility(updateDateOfService: string): void {
    this.loadingEligibility = true;
    // get eligibility link from session storage
    const sessionStoredEligibilityRetrieveLink = this.eligibilityService.getEligibilityRetrieveLinkFromSessionStorage();
    // if we have elgibility link in session storage, then recall the eligibility API to get data using new as of date.
    if (sessionStoredEligibilityRetrieveLink && sessionStoredEligibilityRetrieveLink.href) {
      // Calling LoadingModalComponent for Spinner (Preload Icon)
      openDialog('Loading Coverage...', this.dialog);
      this.eligibilityService.retrieveMemberEligibilityWithSpecifiedAsOfDate(sessionStoredEligibilityRetrieveLink.href, updateDateOfService).subscribe((memberEligibility: Eligibility) => {
        if (memberEligibility) {
          this.lastValidDateOfServiceWherePatientIsEligibileForServices = this.coverageSummaryFormGroup.controls.dateOfService.value;
          this.memberEligibility = memberEligibility;
          if (this.eligibilityProductPackages) {
            this.setDefaultAuthorizedBenefitsCheckboxForEachProductPackageEligibleItem();
          }
        } else {
          this.coverageSummaryFormGroup.controls.dateOfService.setValue(this.lastValidDateOfServiceWherePatientIsEligibileForServices);
        }
      }, () => {
        // on observer error
      }, () => {
        // Close the dialog when the observer completes
        this.dialog.closeAll();
        this.loadingEligibility = false;
      });
    } else {
      this.router.navigate([ApplicationConstants.routing.secure.memberSearchPageUrl]);
    }
  }

  /**
   * Get the eligibility link from session storage, no need to check for validity as that is done in loadEligibility()
   * If the eligibility link is not available and/or valid, this method will not be called and users will be navigated back to member search
   */
  private setMemberNameFromMemberPolicyUsingMatchOnCachedEligibilityLink(): void {
    this.consumerService.getMemberRetrieveResultsOrRecallApiIfDataIsNotInMemory().subscribe( ( memberRetrieveResults: BusinessMemberPoliciesResults ) => {
      if (memberRetrieveResults) {
        let patientFirstName: string = ApplicationConstants.emptyString;
        let patientLastName: string = ApplicationConstants.emptyString;
        const eligibilityLink: Link = this.eligibilityService.getEligibilityRetrieveLinkFromSessionStorage();
        const patientEligibilityHref: string = eligibilityLink.href;
        const memberEligibilityHref: string = (!isNullOrUndefined(memberRetrieveResults.externalEligibilityLink)
          && !isStringNullUndefinedOrEmpty(memberRetrieveResults.externalEligibilityLink.href))
          ? memberRetrieveResults.externalEligibilityLink.href : ApplicationConstants.emptyString;
        if (domainSwap(patientEligibilityHref) === domainSwap(memberEligibilityHref)) {
          patientFirstName = memberRetrieveResults.firstName;
          patientLastName = memberRetrieveResults.lastName;
        } else {
          memberRetrieveResults.dependents.forEach(dependent => {
            if (!isNullOrUndefined(dependent.externalEligibilityLink) && !isStringNullUndefinedOrEmpty(dependent.externalEligibilityLink.href)) {
              if (domainSwap(patientEligibilityHref) === domainSwap(dependent.externalEligibilityLink.href)) {
                patientFirstName = dependent.dependentsName.firstName;
                patientLastName = dependent.dependentsName.lastName;
              }
            }
          });
        }
        this.memberName.firstName = patientFirstName;
        this.memberName.lastName = patientLastName;
      }
    });
  }

  viewPlanSummary(): void {
    const asOfDate = DateUtility.buildFriendlyDateFromJsDate(this.coverageSummaryFormGroup.get('dateOfService').value);
    this.drReportsNavigationService.navigateToVSPPlanSummary(asOfDate, this.memberName.firstName, this.memberName.lastName);
  }

  /***** END - PUBLIC FUNCTIONS *****/


  /***** START - EVENT HANDLERS *****/
  ngOnInit(): void {
    this.buildForm();
    this.buildDatePickerConfiguration();
    this.loadEligibility();
    this.setMemberNameFromMemberPolicyUsingMatchOnCachedEligibilityLink();
  }

  ngOnDestroy(): void {
    // Remove the in-memory cached eligibility on navigation from coverage summary, forcing navigation back to the page
    // to reload the eligibility displayed on the page from the link in session storage
    this.eligibilityService.memberEligibility = undefined;
  }

  /**
   * Toggles the checkbox status for all eligible items for the selected product package.
   *
   * @param productPackage
   */
  toggleAuthorizeBenefitCheckboxForAllProductPackageEligibleItems(productPackage: EligibilityProductPackage): void {
    productPackage.allAvailableServices = !productPackage.allAvailableServices;
    productPackage.eligibleItems.forEach((eligibleItem: EligibleItem) => {
      if (this.isAvailableToAuthorize(eligibleItem)) {
        // For each available service, set the checkbox to match the state of the all available services checkbox
        eligibleItem.isSelected = productPackage.allAvailableServices;
      } else if (eligibleItem.isSelected && eligibleItem.inLieuOfIsSelected) {
        eligibleItem.isSelected = productPackage.allAvailableServices;
        eligibleItem.inLieuOfIsSelected = productPackage.allAvailableServices;
      }
    });
  }

  /**
   * Toggles the checkbox status for all MRR eligible items for the selected product package.
   *
   * @param productPackage
   */
  toggleAuthorizeBenefitCheckboxForAllProductPackageEligibleItemsMrr(productPackage: EligibilityProductPackage): void {
    if (productPackage.mrrEligibleItems && productPackage.mrrEligibleItems.length > 0) {
      productPackage.allAvailableMrrServices = !productPackage.allAvailableMrrServices;
      productPackage.mrrEligibleItems.forEach((mrrEligibleItem: EligibleItem) => {
        // For each available MRR service, set the checkbox to match the state of the all available services checkbox
        mrrEligibleItem.isSelected = productPackage.allAvailableMrrServices;
      });
    }
  }

  /**
   * Toggles the checkbox status for a single eligible item for the selected product package. If all eligible items are
   * in a checked state, update the all available services checkbox to checked. Conversely, if all eligible items are
   * checked and the user un-checks a box, the method will trigger the all available services checkbox to un-check itself.
   *
   * @param eligibleItem
   * @param productPackage
   */
  toggleAuthorizedBenefitCheckboxForSingleEligibleItem(eligibleItem: EligibleItem, productPackage: EligibilityProductPackage): void {
    // Toggle the individual service checkbox, and set all available services checkbox to match the eligible item checked status
    eligibleItem.isSelected = !eligibleItem.isSelected;
    productPackage.allAvailableServices = eligibleItem.isSelected;

    this.updateAvailabilityBasedOnInLieuOf(eligibleItem, productPackage);
    // Iterate through the eligible items on the product package to look for un-checked items
    for (const currentEligibleItemIndex in productPackage.eligibleItems) {
      // Initialize a reference to the current eligible item, and if the item is able to be authorized and is not selected, un-check all available services
      const currentEligibleItem: EligibleItem = productPackage.eligibleItems[currentEligibleItemIndex];
      if (this.isEligibleItemAuthorizableAndNotSelected(currentEligibleItem)) {
        productPackage.allAvailableServices = false;
        break;
      }
    }
  }

  /**
   * For MRR:
   * Toggles the checkbox status for a single eligible item for the selected product package. If all eligible items are
   * in a checked state, update the all available services checkbox to checked. Conversely, if all eligible items are
   * checked and the user un-checks a box, the method will trigger the all available services checkbox to un-check itself.
   *
   * @param mrrEligibleItem
   * @param productPackage
   */

  toggleAuthorizedBenefitCheckboxForSingleEligibleItemMrr(mrrEligibleItem: EligibleItem, productPackage: EligibilityProductPackage): void {
    // Toggle the individual service checkbox, and set all available services checkbox to match the eligible item checked status
    mrrEligibleItem.isSelected = !mrrEligibleItem.isSelected;
    productPackage.allAvailableMrrServices = mrrEligibleItem.isSelected;
    for (const currentEligibleItemIndex in productPackage.mrrEligibleItems) {
      // Initialize a reference to the current eligible item, and if the item is able to be authorized and is not selected, un-check all available services
      const currentEligibleItem: EligibleItem = productPackage.mrrEligibleItems[currentEligibleItemIndex];
      if (this.isEligibleItemAuthorizableAndNotSelected(currentEligibleItem)) {
        productPackage.allAvailableMrrServices = false;
        break;
      }
    }
  }

  /**
   * This method is invoked user interacts with one of the authorizable checkboxes.
   * If the eligible item in question that is passed in has any other eligible items from the same package that are in lieu of this eligible item,
   * then we need to either check and disable the in lieu of eligible item's checkboxes or uncheck and enable the in lieu of eligible item's checkboxes
   * depending on if the eligible item interacted with is being checked or unchecked.
   * @param eligibleItem
   * @param productPackage
   */
  updateAvailabilityBasedOnInLieuOf(eligibleItem: EligibleItem, productPackage: EligibilityProductPackage): void {
    if (!isNullOrUndefined(productPackage.eligibleItemInLieuOfs)) {
      const eligibleItemsInLieOfThisEligibileItem: string[] = productPackage.eligibleItemInLieuOfs.get(eligibleItem.productCatalogKey);
      // check if the eligibile item passed in has any other eligibile items it is in lieu of
      if (eligibleItemsInLieOfThisEligibileItem && eligibleItemsInLieOfThisEligibileItem.length > 0) {
        // Iterate through the eligible items in the package
        productPackage.eligibleItems.forEach((currentEligibleItem: EligibleItem) => {
          // If we find an eligible item that is in lieu of the eligible item passed into this method
          if (eligibleItemsInLieOfThisEligibileItem.includes(currentEligibleItem.productCatalogKey)) {
            // If the eligible item passed in is checked by the user, then we need to set the in lieu of fields to checked and disabled also
            if (eligibleItem.isSelected && this.isAvailableToAuthorize(currentEligibleItem)) {
              currentEligibleItem.isSelected = true;
              currentEligibleItem.inLieuOfIsSelected = true;
            } else {
              currentEligibleItem.isSelected = false;
              currentEligibleItem.inLieuOfIsSelected = false;
            }
          }
        });
      }
    }
  }

  /**
   * Matches on any eligible items having been selected via their respective checkbox and enables the Issue Authorizations
   * button if any have been checked.
   *
   * @param productPackage
   */
  hasAnyEligibleItemsSelected(productPackage: EligibilityProductPackage): boolean {
    // normal items
    if (productPackage.eligibleItems && productPackage.eligibleItems.length > 0 && this.isAnyEligibleItemSelected(productPackage.eligibleItems)) {
      return true;
    }

    // MRR items
    if (productPackage.isMrrFlow && productPackage.mrrEligibleItems && productPackage.mrrEligibleItems.length > 0 &&
      this.isAnyEligibleItemSelected(productPackage.mrrEligibleItems) &&
      // checks if an option from the dropdown is selected.
      this.MRRDropdownItems.some(item => item.value === productPackage.mrrDropdownSelection)) {
      return true;
    }

    // CLRA benefit item
    return this.isClraCheckboxChecked(productPackage);
  }

  /**
   * Enables the Issue Authorization button based on the product package view model property for any available services.
   *
   * @param productPackage
   */
  hasAnyAvailableServices(productPackage: EligibilityProductPackage): boolean {
    return !isNullOrUndefined(productPackage.hasAnyAvailableServices);
  }

  hasAnyAvailableMrrServices(productPackage: EligibilityProductPackage): boolean {
    return !isNullOrUndefined(productPackage.hasAnyAvailableMrrServices);
  }

  /**
   * Used to disable checkboxes that are not available to authorize due to non-available status descriptions.
   *
   * @param eligibleItem
   */
  isAvailableToAuthorize(eligibleItem: EligibleItem): boolean {
    return eligibleItem.authorizable && eligibleItem.statusDescription === EligibilityStatusDescription.YES && !eligibleItem.inLieuOfIsSelected;
  }

  /**
   * Issues an authorization using the VSR link returned from eligibility-web. Retrieves a list of all selected and available
   * to authorize items sent from the UI on the product package, and pushes them on the VSR eligible items payload.
   *
   * @param productPackage
   */
  issueAuthorization(productPackage: EligibilityProductPackage): void {
    // Calling LoadingModalComponent for Spinner (Preload Icon)
    openDialog('Loading Authorization...', this.dialog);
    const selectedEligibleItems: EligibleItemVsrDto[] = [];

    // issue auth for MRR
    if (productPackage.isMrrFlow && productPackage.showMrrAvailableServices) {
      if (productPackage.mrrEligibleItems && productPackage.mrrEligibleItems.length > 0) {
        productPackage.mrrEligibleItems.forEach((eligibleItem: EligibleItem) => {
          if (eligibleItem.isSelected) {
            selectedEligibleItems.push({
              name: eligibleItem.productCatalogKey,
              status: AuthorizedItemStatus.Overridden
            });
          }
        });
      }
    } else if (this.isClraCheckboxChecked(productPackage)) {
      // issue CLRA auth
      selectedEligibleItems.push({
        name: productPackage.clraItem.productCatalogKey,
        status: AuthorizedItemStatus.Overridden
      });


    } else {
      // issue regular services auth
      if (productPackage.eligibleItems && productPackage.eligibleItems.length > 0) {
        productPackage.eligibleItems.forEach((eligibleItem: EligibleItem) => {
          if (eligibleItem.authorizable && eligibleItem.isSelected) {
            selectedEligibleItems.push({name: eligibleItem.productCatalogKey});
          }
        });
      }
    }

    // Check to see if the URL is available on the product package, supplied by eligibility-web
    if (productPackage.createVisionServiceRequest && productPackage.createVisionServiceRequest.href) {
      const vsrLink: string = this.updateVSRLinkReturnedFromEligibilityWithDateOfService(productPackage.createVisionServiceRequest.href);
      const createVsrRequest = new CreateVisionServiceRequest(selectedEligibleItems);
      // Verify the create request contains the necessary information to send in the payload to VSR
      const vsrPayload = createVsrRequest.vsrPayload;

      // MRR
      if (productPackage.isMrrFlow && productPackage.showMrrAvailableServices) {
        vsrPayload.comment = productPackage.mrrDropdownSelection;
        vsrPayload.specialHandlingCodes = [ApplicationConstants.SpecialHandlingCodes.MRR];
      }

      // CLRA
      if (this.isClraCheckboxChecked(productPackage)) {
        vsrPayload.specialHandlingCodes = [ApplicationConstants.SpecialHandlingCodes.CLRA];
      }

      if (vsrPayload) {
        this.authorizationService.createAuthorization(vsrLink, vsrPayload).subscribe((vsrNumber: string) => {
          if (!isStringNullUndefinedOrEmpty(vsrNumber)) {
            this.continueIssueAuthorizationNavigation();
            return;
          }

          this.closeAllPendingDialogs();
        });
      } else { // the payload was not valid
        this.closeAllPendingDialogs();
      }
    } else { // the VSR link on the product package was not available
      this.closeAllPendingDialogs();
    }
  }

  onCoverageSummaryFormSubmit(): void {
    // TODO - implement form submit logic
  }

  updateEligibilityUsingAsOfDateFromDateOfService(): void {
    if (this.coverageSummaryFormGroup.controls.dateOfService.valid) {
      this.reloadEligibility(this.coverageSummaryFormGroup.controls.dateOfService.value);
    }
  }

  toggleShowMrrAvailableBenefits(productPackage: EligibilityProductPackage): void {
    productPackage.showMrrAvailableServices = !productPackage.showMrrAvailableServices;
    if (!productPackage.showMrrAvailableServices) {
      // When "Cancel" button is clicked
      this.uncheckAllEligibleItemCheckboxes(productPackage.mrrEligibleItems);
      productPackage.allAvailableMrrServices = false;
    } else {
      // When "Click here to use benefit" is clicked.
      this.uncheckAllEligibleItemCheckboxes(productPackage.eligibleItems);
      productPackage.allAvailableServices = false;
    }
    productPackage.mrrDropdownSelection = '';
  }

  uncheckAllEligibleItemCheckboxes(eligibleItems: EligibleItem[]): void {
    eligibleItems.forEach(item => {
      item.isSelected = false;
    });
  }

  showMrrAvailableBenefits(productPackage: EligibilityProductPackage): boolean {
    return productPackage.showMrrAvailableServices;
  }

  // method checks if MRR/CLRA flow applies, so it can display "Cancel" button
  shouldDisplayCancelButton(productPackage: EligibilityProductPackage): boolean {
    return this.showMrrAvailableBenefits(productPackage) || productPackage.isClraFlow;
  }

  toggleAuthorizeBenefitCheckboxForCLRA(productPackage: EligibilityProductPackage): void {
    productPackage.clraItem.isSelected = !productPackage.clraItem.isSelected;
  }

  onClickCancelClra(productPackage: EligibilityProductPackage): void {
    // uncheck CLRA and all other services checkboxes.
    productPackage.clraItem.isSelected = false;
    productPackage.allAvailableServices = false;
    this.uncheckAllEligibleItemCheckboxes(productPackage.eligibleItems);
  }

  isClraCheckboxChecked(productPackage: EligibilityProductPackage): boolean {
    return productPackage?.isClraFlow === true && !isNullOrUndefined(productPackage?.clraItem) && productPackage?.clraItem?.isSelected === true;
  }

  // return true if either CLRA checkbox is checked OR any of the available services checkbox is checked.
  disableClraCancelButton(productPackage: EligibilityProductPackage): boolean {
    return !(this.isClraCheckboxChecked(productPackage) || productPackage.eligibleItems.some(item => item.isSelected));
  }

  // checks if the Available Services container for the productPackage should be grayed out.
  shouldGrayOutAvailableServices(productPackage: EligibilityProductPackage): boolean {
    // Return true if MRR services should be displayed OR if CLRA checkbox is selected, otherwise false.
    return productPackage.showMrrAvailableServices || this.isClraCheckboxChecked(productPackage);
  }

  // returns true if the Any Checkbox for EligibleItem[] is checked.
  isAnyEligibleItemSelected(eligibleItems: EligibleItem[]): boolean {
    return eligibleItems.some(item => item.isSelected);
  }

  // Returns true if either condition is true.
  // 1. The eligibleItem is not available to be authorized.
  // 2. If the user is viewing MRR benefits.
  // 3. When the CLRA benefit checkbox is checked.
  shouldDisableAvailableServiceCheckbox(productPackage: EligibilityProductPackage, eligibleItem: EligibleItem): boolean {
    return !this.isAvailableToAuthorize(eligibleItem) || this.showMrrAvailableBenefits(productPackage) || this.isClraCheckboxChecked(productPackage);
  }

  shouldDisableAllAvailableServicesCheckbox(productPackage: EligibilityProductPackage): boolean {
    return !this.hasAnyAvailableServices(productPackage) || this.showMrrAvailableBenefits(productPackage) || this.isClraCheckboxChecked(productPackage);
  }
  /***** END - EVENT HANDLERS *****/
}
